import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAppId } from '../../context/AppIdContext';
import Header from '../../component/header';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const SlipPromotion = () => {
    const [slips, setSlips] = useState([]);
    const [loading, setLoading] = useState(false);
    const [uploadMode, setUploadMode] = useState('ADD');

    const { appId, language, userId, userType } = useAppId();
    const lang = language || 'en';
    const fileInputRef = useRef(null);

    const activeRole = userType || localStorage.getItem('userType') || localStorage.getItem('designation') || localStorage.getItem('role');
    const roleCode = activeRole ? activeRole.toUpperCase() : 'A';
    const isSubAdmin = roleCode === 'SA' || roleCode === 'SUB_ADMIN';

    const t = {
        en: {
            pageTitle: "Slip Promotion",
            subtitle: "View and share promotional slips",
            uploadedBy: "Uploaded :",
            loadingData: "Loading Slips...",
            noData: "No promotional slips found.",
            btnChange: "Change Image",
            btnDelete: "Delete Image",
            btnAdd: "Add Slip"
        },
        hi: {
            pageTitle: "पर्ची प्रचार",
            subtitle: "प्रचार पर्ची देखें और साझा करें",
            uploadedBy: "अपलोड :",
            loadingData: "पर्चियां लोड हो रही हैं...",
            noData: "कोई प्रचार पर्ची नहीं मिली।",
            btnChange: "छवि बदलें",
            btnDelete: "छवि हटाएं",
            btnAdd: "पर्ची जोड़ें"
        },
        gu: {
            pageTitle: "સ્લિપ પ્રમોશન",
            subtitle: "પ્રમોશનલ સ્લિપ્સ જુઓ અને શેર કરો",
            uploadedBy: "અપલોડ :",
            loadingData: "સ્લિપ્સ લોડ થઈ રહી છે...",
            noData: "કોઈ પ્રમોશનલ સ્લિપ મળી નથી.",
            btnChange: "છબી બદલો",
            btnDelete: "છબી કાઢી નાખો",
            btnAdd: "સ્લિપ ઉમેરો"
        }
    };

    const txt = t[lang];

    const fetchSlips = async () => {
        if (!appId) return;

        setLoading(true);
        try {
            const response = await axios.post(
                `${BASE_URL}/api/dis_prachar_master`,
                { 
                    app_id: appId,
                    prachar_type: "sleep" // Note: Backend uses 'sleep' for slip
                }
            );

            if (response.data.success && response.data.data && Array.isArray(response.data.data.result)) {
                setSlips(response.data.data.result);
            } else {
                setSlips([]);
            }
        } catch (error) {
            console.error("Error fetching slip promotions:", error);
            setSlips([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSlips();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appId]);

    const handleAddImage = () => {
        setUploadMode('ADD');
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleChangeImage = () => {
        setUploadMode('CHANGE');
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    // --- 🔴 નવું લોજિક: સીધું જ CDN પર ફાઈલ મોકલીને પછી ડેટાબેઝમાં એન્ટ્રી કરવી ---
    const handleFileChange = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        setLoading(true);

        try {
            // ૧. FormData બનાવીને CDN પર ફાઈલ અપલોડ કરવી
            const formData = new FormData();
            formData.append('api_key', 'BJP-Media-7Kx9Qm2pR4vN8wL3jH6tY5cF1bZ0aD');
            formData.append('app_id', appId?.toString() || "1");
            formData.append('user_id', userId?.toString() || "1");
            formData.append('folder', 'files');
            formData.append('file', file);

            console.log("Uploading slip to CDN...");

            const cdnResponse = await axios.post(
                'https://cdn.bjpwings.com/ResourceUploadHandler.ashx',
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );

            console.log("CDN Response:", cdnResponse.data);

            // ૨. જો CDN પર અપલોડ સફળ થાય, તો ins_prachar_master માં એન્ટ્રી કરવી
            if (cdnResponse.data && cdnResponse.data.Success === "1" && cdnResponse.data.files && cdnResponse.data.files.length > 0) {
                
                const uploadedFile = cdnResponse.data.files[0];

                const payload = {
                    app_id: appId?.toString(),
                    prachar_type: "SLEEP", // Backend uses SLEEP
                    base64: "", // હવે Base64 ની જરૂર નથી
                    content: uploadedFile.file_url, // 🔴 CDN ની લિંક અહીં પાસ કરી
                    create_by: userId?.toString() || "1",
                    cdn_response: cdnResponse.data
                };

                // જો પહેલેથી સ્લિપ હોય અને Change Image પર ક્લિક કર્યું હોય તો, જૂનો ID પાસ કરવો
                if (uploadMode === 'CHANGE' && slips.length > 0) {
                    payload.id = slips[0].id.toString();
                }

                console.log("Saving slip to database payload:", payload);

                const response = await axios.post(
                    `${BASE_URL}/api/ins_prachar_master`,
                    payload
                );

                const resData = response.data;
                if (resData.Success === "1" || resData.Success === 1 || resData.success === true) {
                    await fetchSlips();
                } else {
                    alert("Failed to save the image. Server responded but success was false.");
                }
            } else {
                alert("Failed to upload image to CDN server.");
            }
        } catch (error) {
            console.error("Error uploading image:", error);
            alert("An error occurred while uploading the image.");
        } finally {
            setLoading(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const handleDeleteImage = async () => {
        if (slips.length === 0) return;

        if (window.confirm("Are you sure you want to delete this slip photo?")) {
            try {
                const photoId = slips[0].id; 
                
                const response = await axios.post(
                    `${BASE_URL}/api/del_prachar_master`,
                    {
                        app_id: appId,
                        id: photoId?.toString(),
                        delete_by: userId?.toString() || "1"
                    }
                );

                const resData = response.data;
                if (resData.Success === "1" || resData.Success === 1 || resData.success === true) {
                    fetchSlips();
                } else {
                    alert("Failed to delete the image.");
                }
            } catch (error) {
                console.error("Error deleting image:", error);
                alert("An error occurred while deleting the image.");
            }
        }
    };

    if (loading && slips.length === 0) {
        return (
            <div className="app-container p-4">
                <Header title={txt.pageTitle}/>
                <div className="loader-wrapper">
                    <div className="spinner-modern"></div>
                    <p className="loader-text">{txt.loadingData}</p>
                </div>
                <style jsx>{`
                    .app-container { background: #E5E7EB; min-height: 100vh; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; }
                    .loader-wrapper { display: flex; flex-direction: column; justify-content: center; align-items: center; min-height: 60vh; width: 100%; }
                    .spinner-modern { width: 50px; height: 50px; border: 4px solid #cbd5e1; border-top: 4px solid #10b981; border-radius: 50%; animation: spin 1s linear infinite; margin-bottom: 16px; }
                    .loader-text { color: #64748b; font-weight: 600; font-size: 1.1rem; }
                    @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
                `}</style>
            </div>
        );
    }

    return (
        <div className='app-container p-4'>

            {/* Header */}
            <Header title={txt.pageTitle} />

            {/* Page Action Bar */}
            <div className="page-action-bar mt-3 mb-4">
                <div className="subtitle-container m-0">
                    <p className="subtitle-text">{txt.subtitle}</p>
                </div>
                
                <div className="global-buttons">
                    <input 
                        type="file" 
                        accept="image/*" 
                        ref={fileInputRef} 
                        style={{ display: 'none' }} 
                        onChange={handleFileChange} 
                    />

                    {!isSubAdmin && (
                        slips.length === 0 ? (
                            <button 
                                className="global-btn btn-add" 
                                onClick={handleAddImage}
                                disabled={loading}
                            >
                                <svg fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                                    <line x1="12" y1="5" x2="12" y2="19"></line>
                                    <line x1="5" y1="12" x2="19" y2="12"></line>
                                </svg>
                                {txt.btnAdd}
                            </button>
                        ) : (
                            <>
                                <button 
                                    className="global-btn btn-edit" 
                                    onClick={handleChangeImage}
                                    disabled={loading}
                                >
                                    <svg fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                                        <path d="M12 20h9"></path>
                                        <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path>
                                    </svg>
                                    {txt.btnChange}
                                </button>

                                <button 
                                    className="global-btn btn-delete" 
                                    onClick={handleDeleteImage}
                                    disabled={loading}
                                >
                                    <svg fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                                        <polyline points="3 6 5 6 21 6"></polyline>
                                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                        <line x1="10" y1="11" x2="10" y2="17"></line>
                                        <line x1="14" y1="11" x2="14" y2="17"></line>
                                    </svg>
                                    {txt.btnDelete}
                                </button>
                            </>
                        )
                    )}
                </div>
            </div>

            {/* No Data State */}
            {slips.length === 0 && !loading && (
                <div className="row">
                    <div className="col-12 text-center text-muted p-5 empty-state">
                        <div className="empty-icon">🧾</div>
                        <p>{txt.noData}</p>
                    </div>
                </div>
            )}

            {/* --- SLIP GRID --- */}
            <div className="slip-grid pb-5">
                {slips.map((slip) => (
                    <div className="slip-card" key={slip.id}>
                        {/* Image Container */}
                        <div className="image-wrapper">
                            <img 
                                src={slip.content} 
                                alt={slip.name} 
                                className="promo-image"
                                loading="lazy"
                                onError={(e) => {
                                    const fallbackUrl = "data:image/svg+xml;charset=UTF-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300' viewBox='0 0 400 300'%3E%3Crect width='100%25' height='100%25' fill='%23f1f5f9'/%3E%3Ctext x='50%25' y='50%25' font-family='sans-serif' font-size='20' font-weight='bold' fill='%2394a3b8' text-anchor='middle' dy='.3em'%3EImage Not Found%3C/text%3E%3C/svg%3E";
                                    if (e.target.src !== fallbackUrl) {
                                        e.target.src = fallbackUrl;
                                    }
                                }}
                            />
                        </div>

                        {/* Details Container */}
                        <div className="info-wrapper">
                            <div className="upload-info">
                                <span className="upload-label">{txt.uploadedBy}</span>
                                <span className="upload-name">{slip.create_by_name || slip.name || "Unknown"}</span>
                            </div>
                            <div className="designation-badge">
                                {slip.designation || "User"}
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <style jsx>{`
                .app-container {
                    background: #E5E7EB;
                    min-height: 100vh;
                    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
                }

                .page-action-bar { display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 16px; }
                .subtitle-text { font-size: 15px; color: #475569; font-weight: 600; margin: 0; text-transform: uppercase; letter-spacing: 0.5px; }
                .global-buttons { display: flex; gap: 12px; }

                .global-btn { display: flex; align-items: center; gap: 8px; padding: 10px 16px; border-radius: 10px; font-size: 13px; font-weight: 600; cursor: pointer; transition: all 0.2s ease; border: none; box-shadow: 0 1px 2px rgba(0,0,0,0.05); }
                .global-btn svg { width: 16px; height: 16px; }
                
                .global-btn:disabled { opacity: 0.7; cursor: not-allowed; }

                .btn-add { background: #10b981; color: #ffffff; border: 1px solid #059669; box-shadow: 0 4px 6px -1px rgba(16, 185, 129, 0.2); }
                .btn-add:hover:not(:disabled) { background: #059669; border-color: #047857; transform: translateY(-1px); box-shadow: 0 6px 8px -1px rgba(16, 185, 129, 0.3); }
                .btn-add:active:not(:disabled) { background: #047857; transform: translateY(0); }

                .btn-edit { background: #ffffff; color: #334155; border: 1px solid #cbd5e1; }
                .btn-edit:hover:not(:disabled) { background: #f8fafc; color: #0f172a; border-color: #94a3b8; }
                .btn-edit:active:not(:disabled) { background: #f1f5f9; }

                .btn-delete { background: #fef2f2; color: #ef4444; border: 1px solid #fecaca; }
                .btn-delete:hover:not(:disabled) { background: #fee2e2; color: #dc2626; border-color: #fca5a5; }
                .btn-delete:active:not(:disabled) { background: #fecaca; }

                .slip-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 24px; }

                .slip-card { background: #ffffff; border-radius: 16px; overflow: hidden; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03); border: 1px solid #f1f5f9; display: flex; flex-direction: column; transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1); cursor: pointer; }
                .slip-card:hover { transform: translateY(-5px); box-shadow: 0 15px 30px -5px rgba(0, 0, 0, 0.1); border-color: #e2e8f0; }

                .image-wrapper { width: 100%; height: 220px; background: #f8fafc; overflow: hidden; position: relative; display: flex; align-items: center; justify-content: center; }
                .promo-image { width: 100%; height: 100%; object-fit: contain; transition: transform 0.5s ease; }
                .slip-card:hover .promo-image { transform: scale(1.03); }

                .info-wrapper { padding: 16px 20px; display: flex; flex-direction: column; gap: 8px; background: #ffffff; border-top: 1px solid #f1f5f9; }
                .upload-info { display: flex; align-items: baseline; gap: 6px; flex-wrap: wrap; }
                .upload-label { font-size: 13px; color: #64748b; font-weight: 500; }
                .upload-name { font-size: 15px; color: #0f172a; font-weight: 700; }

                .designation-badge { align-self: flex-start; font-size: 11px; font-weight: 700; color: #10b981; background: #ecfdf5; padding: 4px 10px; border-radius: 20px; text-transform: uppercase; letter-spacing: 0.5px; border: 1px solid #a7f3d0; }

                .empty-state { background: white; border-radius: 20px; border: 2px dashed #cbd5e1; }
                .empty-icon { font-size: 3rem; margin-bottom: 10px; opacity: 0.5; }

                @media (max-width: 640px) {
                    .page-action-bar { flex-direction: column; align-items: flex-start; gap: 12px; }
                    .global-buttons { width: 100%; display: flex; }
                    .global-btn { flex: 1; justify-content: center; }
                    .slip-grid { grid-template-columns: 1fr; gap: 16px; }
                    .image-wrapper { height: 250px; }
                }
            `}</style>
        </div>
    );
};

export default SlipPromotion;